import { useState, useEffect } from "react";
import { X, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { trackOnce } from "../lib/pixel";

const MIN_EQUITY = 1000;

// Phase 8 — Request Subscription Upgrade: capture declared equity (blank by default),
// enforce the $1,000 minimum server-side, persist the request, then open a prefilled
// WhatsApp chat directly from the user's click (popup-safe).
export default function UpgradeRequestModal({ open, onClose }) {
  const [equity, setEquity] = useState("");
  const [busy, setBusy] = useState(false);

  // Always open blank — never prefill with any previously declared figure.
  useEffect(() => { if (open) setEquity(""); }, [open]);

  if (!open) return null;
  const val = Number(equity);
  const valid = equity !== "" && !Number.isNaN(val) && val >= MIN_EQUITY;

  const submit = async () => {
    if (!valid) { toast.error(`Minimum eligibility is USD $${MIN_EQUITY.toLocaleString()}`); return; }
    if (busy) return;
    setBusy(true);
    // Open the tab synchronously inside the click so the browser does not block it.
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post("/subscription/upgrade-request", { equity: val });
      if (!data?.duplicate && data?.request_id) trackOnce("Lead", data.request_id);
      const url = data?.whatsapp_url;
      if (url) {
        if (win && !win.closed) win.location.href = url;
        else window.open(url, "_blank", "noopener");
      } else if (win && !win.closed) {
        win.close();
      }
      toast.success(data?.duplicate
        ? `Your request ${data.request_id} is already pending admin review — opening WhatsApp.`
        : "Request saved — opening WhatsApp to continue the conversation.");
      onClose();
    } catch (e) {
      if (win && !win.closed) win.close();
      toast.error(e.response?.data?.detail || "Could not submit request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="upgrade-request-modal" onClick={onClose}>
      <div className="card-dark w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X size={16} /></button>
        <div className="flex items-center gap-2 mb-1"><TrendingUp size={16} className="text-[#00F0FF]" /><p className="text-lg text-white">Request Subscription Upgrade</p></div>
        <p className="text-xs text-gray-400 mb-4 leading-relaxed">ChartzFx eligibility requires a minimum trading equity of <b className="text-white">USD ${MIN_EQUITY.toLocaleString()}</b>. Profit share is 50/50 of eligible net profits, settled every Friday.</p>
        <label className="label-xs">Your current trading equity (USD)</label>
        <input data-testid="upgrade-equity-input" type="number" min="0" step="1" value={equity} onChange={(e) => setEquity(e.target.value)}
          className="input-dark !py-2.5 text-sm mt-1" placeholder="e.g. 1000" autoFocus />
        {equity !== "" && !valid && <p className="text-[11px] text-[#FF3B30] mt-1" data-testid="upgrade-equity-error">Minimum eligibility is USD ${MIN_EQUITY.toLocaleString()}.</p>}
        <button data-testid="upgrade-submit-btn" onClick={submit} disabled={!valid || busy}
          className="btn-primary w-full py-2.5 text-sm mt-5 disabled:opacity-50">{busy ? "Submitting…" : "Request Subscription Upgrade"}</button>
        <p className="text-[11px] text-gray-600 mt-3 text-center">We'll save your request, notify our team, and open WhatsApp with a prefilled message.</p>
      </div>
    </div>
  );
}
